import { mergeTranslations, sanitizeTranslations } from '../languages.js';
import { buildSubjectImagePrompt, wantsCardImage } from './chromatics.js';
import { conjugateVerb } from '../nlp/conjugation.js';
import { expandKnownForms } from '../nlp/forms.js';
import { stripArticleIpa } from '../nlp/pronunciation.js';
const SENSE_KEY = /^[a-z0-9_]{0,40}$/;
/** Keeps the well-formed entries of a list of meanings sent by the server. */
export function validateWordSenses(raw) {
    if (!Array.isArray(raw))
        return [];
    const senses = [];
    for (const entry of raw.slice(0, 12)) {
        const e = (entry ?? {});
        const label = text(e.label, 60);
        if (typeof e.sense_key !== 'string' || !SENSE_KEY.test(e.sense_key) || !label)
            continue;
        if (senses.some((s) => s.sense_key === e.sense_key))
            continue;
        senses.push({
            sense_key: e.sense_key,
            label,
            hint: text(e.hint, 40) ?? '',
            article: text(e.article, 3),
            plural: text(e.plural, 80),
            part_of_speech: text(e.part_of_speech, 20) ?? 'other',
            ready: e.ready === true,
        });
    }
    return senses;
}
const POS = ['noun', 'verb', 'adjective', 'adverb', 'phrase', 'other'];
const GENDERS = ['masculine', 'feminine', 'neuter'];
const ARTICLE_FOR = { masculine: 'der', feminine: 'die', neuter: 'das' };
const WORD = /^[\p{L}][\p{L}()'’-]*$/u;
/** A verb form: one word, or a few words for a verb written in two ("gehe spazieren"). */
const FORM = /^[\p{L}][\p{L}()'’-]*(?: [\p{L}][\p{L}()'’-]*){0,2}$/u;
function text(value, max) {
    if (typeof value !== 'string')
        return null;
    const clean = value.replace(/\s+/g, ' ').trim();
    return clean && clean.length <= max ? clean : null;
}
function wordList(value) {
    if (!Array.isArray(value) || value.length !== 6)
        return null;
    const forms = value.map((v) => text(v, 40));
    return forms.every((f) => !!f && FORM.test(f)) ? forms : null;
}
function validateVerb(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const v = raw;
    const present = wordList(v.present);
    const preterite = wordList(v.preterite);
    const participle = text(v.participle, 40);
    const k2 = text(v.konjunktiv2_ich, 30);
    // Modal verbs (können, müssen, mögen ...) have no imperative: it may be empty.
    const imp = text(v.imperative_du, 30)?.replace(/!$/, '') ?? '';
    const prefix = typeof v.separable_prefix === 'string' ? v.separable_prefix.trim() : '';
    if (v.auxiliary !== 'haben' && v.auxiliary !== 'sein')
        return null;
    if (!present || !preterite || !participle || !k2)
        return null;
    if (!FORM.test(participle) || !FORM.test(k2) || (imp && !FORM.test(imp)))
        return null;
    if (prefix && !/^\p{L}{1,12}$/u.test(prefix))
        return null;
    // The prefix is added behind the forms later: it must not be in them twice.
    const bare = (f) => prefix && f.endsWith(` ${prefix}`) ? f.slice(0, -prefix.length - 1) : f;
    return {
        auxiliary: v.auxiliary,
        participle,
        present: present.map(bare),
        preterite: preterite.map(bare),
        konjunktiv2_ich: bare(k2),
        imperative_du: imp ? bare(imp) : '',
        separable_prefix: prefix,
    };
}
/**
 * Checks AI/cache output before it becomes a card. Anything malformed is rejected rather than
 * patched, so a bad generation never produces a half-broken card.
 */
export function validateGeneratedContent(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const r = raw;
    const lemma = text(r.lemma, 80);
    const translation = text(r.translation, 120);
    // A phrase or sentence may come without a pronunciation line (it would be too long).
    const ipa = text(r.ipa, 100) ?? (r.part_of_speech === 'phrase' && !r.ipa ? '' : null);
    const exampleDe = text(r.example_de, 300);
    const exampleEn = text(r.example_en, 300);
    const pos = POS.includes(r.part_of_speech)
        ? r.part_of_speech
        : null;
    if (!lemma || !translation || ipa === null || !exampleDe || !exampleEn || !pos)
        return null;
    let gender = null;
    let article = null;
    let plural = null;
    let pluralIpa = null;
    if (pos === 'noun') {
        plural = text(r.plural, 80);
        if (plural && !/^die \S/.test(plural))
            plural = `die ${plural}`;
        if (GENDERS.includes(r.gender)) {
            gender = r.gender;
            article = ARTICLE_FOR[gender];
        }
        else if (!((r.gender === 'none' || r.gender == null) && plural)) {
            // Only a noun that exists in the plural alone (die Großeltern) may come without a gender
            // ('none' from the model, null once stored).
            return null;
        }
        // Stored without the article (the AI sometimes includes it); the article is added for display.
        pluralIpa = plural ? stripArticleIpa(text(r.plural_ipa, 100)) : null;
    }
    let verb = null;
    if (pos === 'verb') {
        verb = validateVerb(r.verb);
        if (!verb)
            return null;
    }
    const imageSubject = wantsCardImage(pos) ? text(r.image_subject, 160) : '';
    if (imageSubject === null)
        return null;
    const imageUrl = typeof r.image_url === 'string' && r.image_url.startsWith('https://') ? r.image_url : null;
    return {
        lemma,
        language: text(r.language, 10) ?? 'de',
        part_of_speech: pos,
        translation,
        ipa: !ipa ? '' : ipa.startsWith('/') ? ipa : `/${ipa.replace(/^\[|\]$/g, '')}/`,
        gender,
        article,
        plural,
        plural_ipa: pluralIpa,
        example_de: exampleDe,
        example_en: exampleEn,
        image_subject: imageSubject,
        verb,
        image_url: imageUrl,
        translations: sanitizeTranslations(r.translations),
        sense_key: typeof r.sense_key === 'string' && SENSE_KEY.test(r.sense_key) ? r.sense_key : '',
        sense_hint: text(r.sense_hint, 40),
        other_senses: validateWordSenses(r.other_senses),
    };
}
/** Maps the AI's principal parts onto the deterministic conjugator's spec. */
export function verbSpecFromGenerated(infinitive, verb) {
    const particle = verb.separable_prefix || undefined;
    const base = particle ? infinitive.slice(particle.length).trim() : infinitive;
    return {
        infinitive,
        auxiliary: verb.auxiliary,
        participle: verb.participle,
        present: verb.present,
        preterite: verb.preterite,
        konjunktiv2Stem: verb.konjunktiv2_ich.replace(/e$/, ''),
        // A verb without an imperative (a modal verb) leaves the whole row empty.
        imperative: verb.imperative_du
            ? {
                du: verb.imperative_du,
                wir: `${base} wir`,
                ihr: verb.present[4],
                sie: `${base} Sie`,
            }
            : { du: '', wir: '', ihr: '', sie: '' },
        separable: particle,
    };
}
/** Builds a fresh card (new FSRS state) for `userId` from validated generated content. */
export function cardFromGeneratedContent(content, userId, now = new Date()) {
    const nowIso = now.toISOString();
    const gender = content.gender ?? 'neutral';
    const spec = content.verb ? verbSpecFromGenerated(content.lemma, content.verb) : undefined;
    const conjugation = spec ? conjugateVerb(spec) : undefined;
    const visual = wantsCardImage(content.part_of_speech)
        ? {
            ...buildSubjectImagePrompt(content.image_subject, gender),
            image_subject: content.image_subject,
        }
        : buildSubjectImagePrompt(content.lemma, gender);
    const linguistic_meta = {
        gender: content.gender,
        article: content.article,
        plural: content.plural,
        plural_ipa: content.plural_ipa ?? undefined,
        part_of_speech: content.part_of_speech,
        phonetic_ipa: content.ipa,
        conjugation,
        content_source: 'ai',
    };
    linguistic_meta.forms = expandKnownForms({
        lemma: content.lemma,
        part_of_speech: content.part_of_speech,
        linguistic_meta,
        separable: spec?.separable,
    });
    return {
        id: crypto.randomUUID ? crypto.randomUUID() : `card_${Date.now()}_${Math.random()}`,
        user_id: userId,
        target_language: content.language,
        lemma: content.lemma,
        front_text: content.lemma,
        back_text: content.translation,
        part_of_speech: content.part_of_speech,
        phonetic_ipa: content.ipa,
        linguistic_meta,
        visual_meta: visual,
        image_url: wantsCardImage(content.part_of_speech)
            ? (content.image_url ?? undefined)
            : undefined,
        example_target: content.example_de,
        example_native: content.example_en,
        translations: content.translations,
        sense_key: content.sense_key ?? '',
        sense_hint: content.sense_hint ?? null,
        fsrs_state: 'New',
        stability: 0,
        difficulty: 0,
        reps: 0,
        lapses: 0,
        last_review: null,
        due_date: nowIso,
        created_at: nowIso,
        updated_at: nowIso,
    };
}
/** Replaces a card's content with freshly generated content, keeping its id and review history. */
export function refreshCardContent(card, content) {
    const fresh = cardFromGeneratedContent(content, card.user_id);
    return {
        ...card,
        lemma: fresh.lemma,
        front_text: fresh.front_text,
        back_text: fresh.back_text,
        part_of_speech: fresh.part_of_speech,
        phonetic_ipa: fresh.phonetic_ipa,
        linguistic_meta: fresh.linguistic_meta,
        visual_meta: fresh.visual_meta,
        image_url: fresh.image_url,
        example_target: fresh.example_target,
        example_native: fresh.example_native,
        translations: mergeTranslations(card.translations, fresh.translations),
        sense_key: fresh.sense_key,
        sense_hint: fresh.sense_hint,
        updated_at: new Date().toISOString(),
    };
}
